import { useState, useCallback, useMemo } from 'react';
import { useApi } from '../auth/useApi';
import { 
    DashboardResponse, 
    HarvesterDetailResponse, 
    BenchmarkResponse, 
    FilterOptions, 
    DashboardFilters,
    SortableColumns,
    SortOrder,
    PemanenResponse
} from './types';
import { 
    PERFORMANCE_ENDPOINTS, 
    USER_ENDPOINTS,
    buildUrlWithParams,
    buildDashboardParams,
    buildDetailParams,
    buildBenchmarkParams
} from './apiConfig';

export const useHarvestersPerformance = (filters: DashboardFilters) => {
    const [sortBy, setSortBy] = useState<SortableColumns>('total_score');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Build API URL with parameters
    const buildDashboardUrl = useCallback(() => {
        const params = buildDashboardParams({
            startDate: filters.startDate,
            endDate: filters.endDate,
            estateId: filters.estateId,
            divisiId: filters.divisiId,
            limit: filters.limit
        });
        
        return buildUrlWithParams(PERFORMANCE_ENDPOINTS.DASHBOARD, params);
    }, [filters]);

    // Fetch dashboard data
    const {
        data: dashboardData,
        loading: dashboardLoading,
        error: dashboardError,
        refetch: refetchDashboard
    } = useApi<DashboardResponse>(buildDashboardUrl(), {}, true);

    // Fetch filter options
    const {
        data: filterOptions,
        loading: filterLoading,
        error: filterError
    } = useApi<FilterOptions>(PERFORMANCE_ENDPOINTS.FILTERS, {}, true);

    // Sorted harvesters
    const sortedHarvesters = useMemo(() => {
        if (!dashboardData?.harvesters) return [];
        
        return [...dashboardData.harvesters].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];
            
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
            }
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'desc' 
                    ? bValue.localeCompare(aValue) 
                    : aValue.localeCompare(bValue);
            }
            
            return 0;
        });
    }, [dashboardData?.harvesters, sortBy, sortOrder]);

    // Handle sorting
    const handleSort = useCallback((column: SortableColumns) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    }, [sortBy, sortOrder]);

    // Available divisis for selected estate
    const availableDivisis = useMemo(() => {
        if (!filterOptions?.divisis || !filters.estateId) return [];
        return filterOptions.divisis.filter(div => div.estate_id === filters.estateId);
    }, [filterOptions?.divisis, filters.estateId]);

    return {
        // Data
        dashboardData,
        filterOptions,
        sortedHarvesters,
        availableDivisis,
        
        // Loading states
        dashboardLoading,
        filterLoading,
        
        // Errors
        dashboardError,
        filterError,
        
        // Actions
        refetchDashboard,
        handleSort,
        
        // Sorting state
        sortBy,
        sortOrder,
    };
};

export const useHarvesterDetail = (pemanenId: number, startDate?: string, endDate?: string) => {
    const buildDetailUrl = useCallback(() => {
        const params = buildDetailParams({
            startDate,
            endDate
        });
        
        return buildUrlWithParams(PERFORMANCE_ENDPOINTS.DETAIL(pemanenId), params);
    }, [pemanenId, startDate, endDate]);

    return useApi<HarvesterDetailResponse>(buildDetailUrl(), {}, true);
};

export const useBenchmarkData = (filters: Partial<DashboardFilters>) => {
    const buildBenchmarkUrl = useCallback(() => {
        const params = buildBenchmarkParams({
            startDate: filters.startDate,
            endDate: filters.endDate,
            estateId: filters.estateId,
            divisiId: filters.divisiId
        });
        
        return buildUrlWithParams(PERFORMANCE_ENDPOINTS.BENCHMARK, params);
    }, [filters]);

    return useApi<BenchmarkResponse>(buildBenchmarkUrl(), {}, true);
};

// Hook for fetching all pemanen data
export const usePemanenData = () => {
    return useApi<PemanenResponse>(USER_ENDPOINTS.PEMANEN, {}, true);
};
